import { ErrorMessage } from "@/components/common/Error.tsx";
import { Markdown } from "@/components/markdown/Markdown.tsx";
import type { GetArtifactResp, ListArtifactsResp } from "@/types/artifacts.ts";
import { Mail } from "lucide-react";
import { useEffect } from "react";
import { useArtifacts } from "../../hooks/useArtifacts.tsx";
import { Loader } from "../loader/Loader.tsx";

const MessageArtifactItem = ({ artifact }: { artifact: GetArtifactResp }) => {
  let item: {
    url: string | undefined;
    title: string | undefined;
    summary: string | undefined;
  } | null = null;
  switch (artifact.artifactType) {
    case "document": {
      item = {
        url: undefined,
        title: artifact.title ?? "Untitled",
        summary: artifact.content,
      };
      break;
    }
    case "applet": {
      item = {
        url: undefined,
        title: artifact.title ?? "Untitled",
        summary: undefined,
      };
      break;
    }
    case "emailDraft": {
      item = {
        url: undefined,
        title: artifact.subject,
        summary: artifact.body,
      };
      break;
    }
    case "invitation": {
      item = {
        url: undefined,
        title: artifact.summary,
        summary: artifact.description,
      };
      break;
    }
    default:
      return null;
  }

  if (!item) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="text-lg font-semibold">{item.title}</div>
        {item.summary && (
          <div className="text-sm text-muted-foreground">
            <Markdown>{item.summary}</Markdown>
          </div>
        )}
      </div>
      <div className="flex flex-row gap-2 items-center">
        <Mail className="w-4 h-4" />
        <div className="text-sm">Task: {artifact.artifactTypeTitle}</div>
      </div>
    </div>
  );
};
/**
 * Fetches and renders a list of tasks based on provided IDs.
 * Handles loading and error states.
 * Calls `onTasksLoaded` with the fetched tasks once loaded.
 */
export const MessageArtifactList = ({
  threadId,
  messageId,
  onArtifactsLoaded,
}: {
  threadId: number;
  messageId: number;
  onArtifactsLoaded: (artifacts: ListArtifactsResp) => void;
}) => {
  const { artifactsResponse, isLoading, error } = useArtifacts({
    threadId,
    messageId,
  });

  useEffect(() => {
    if (artifactsResponse) {
      onArtifactsLoaded(artifactsResponse);
    }
  }, [artifactsResponse, onArtifactsLoaded]);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  if (!artifactsResponse || artifactsResponse.artifacts.length === 0) {
    return <div className="p-4 text-center text-gray-500">No items found.</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      {artifactsResponse.artifacts.map((artifact) => (
        <MessageArtifactItem key={artifact.id} artifact={artifact} />
      ))}
    </div>
  );
};
